import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { AdminRsvp, RequestStatus } from "@tih/shared";
import { adminFetchRsvps, adminUpdateRsvp } from "../../api/client";

type RsvpFilter = "all" | "unassigned";

export function AdminRsvpsPage() {
  const [rows, setRows] = useState<AdminRsvp[]>([]);
  const [filter, setFilter] = useState<RsvpFilter>("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setRows(
        await adminFetchRsvps(filter === "unassigned" ? { eventId: null } : undefined),
      );
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [filter]);

  async function setStatus(id: number, status: RequestStatus) {
    await adminUpdateRsvp(id, status);
    await load();
  }

  const totalSeats = rows.reduce(
    (sum, r) => sum + (r.totalSeats ?? r.seats),
    0,
  );
  const unassignedCount = useMemo(
    () =>
      filter === "all"
        ? rows.filter((r) => r.reservationCount === 0 || r.eventId == null).length
        : rows.filter((r) => r.eventId == null).length,
    [rows, filter],
  );

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>All RSVPs</h1>
        <p>
          {filter === "all"
            ? `${rows.length} people · ${totalSeats} seats across events. Per-event reservations are managed from each `
            : `${rows.length} unassigned reservations · ${totalSeats} seats. Per-event reservations are managed from each `}
          <Link to="/admin/events">event hub</Link>.
        </p>
      </header>

      <div className="admin-gallery-filters" style={{ marginBottom: 16 }}>
        {(
          [
            ["all", "All RSVPs"],
            ["unassigned", "Unassigned"],
          ] as const
        ).map(([f, label]) => (
          <button
            key={f}
            type="button"
            className={`admin-gallery-filter${filter === f ? " admin-gallery-filter--active" : ""}`}
            onClick={() => setFilter(f)}
          >
            {label}
            {f === "unassigned" && filter !== "unassigned" && unassignedCount > 0
              ? ` (${unassignedCount})`
              : ""}
          </button>
        ))}
      </div>

      {error && <div className="form-error">{error}</div>}
      {loading ? (
        <div style={{ opacity: 0.5 }}>Loading…</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Event</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Seats</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={filter === "all" ? r.rsvpId : r.id}>
                  <td>{new Date(r.createdAt).toLocaleString()}</td>
                  <td>
                    {r.eventId != null ? (
                      <Link to={`/admin/events/${r.eventId}/rsvps`}>
                        {r.eventLabel ?? `Event #${r.eventId}`}
                        {filter === "all" && (r.reservationCount ?? 0) > 1
                          ? ` (+${(r.reservationCount ?? 0) - 1} more)`
                          : ""}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{r.name}</td>
                  <td>{r.email}</td>
                  <td>{r.phone ?? "—"}</td>
                  <td>{r.seats}</td>
                  <td>
                    <select
                      value={r.status}
                      onChange={(e) => void setStatus(r.id, e.target.value as RequestStatus)}
                      className={`status-select status-${r.status.toLowerCase()}`}
                    >
                      <option value="NEW">New</option>
                      <option value="IN_PROGRESS">In progress</option>
                      <option value="DONE">Done</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
